import { loggingData } from 'app/libs/sentry';
import loadScript from './loadScript';
import { deleteCookie, getCookie, setCookie } from './cookie';

export const NAVER_CLIENT_KEY = process.env.REACT_APP_KEY_NAVER_AUTH;
export const KAKAO_CLIENT_KEY = process.env.REACT_APP_KEY_KAKAO_AUTH;
export const GOOGLE_CLIENT_KEY = process.env.REACT_APP_KEY_GOOGLE_AUTH;

export const getNaverRedirectUrl = () => `${window.location.protocol}//${window.location.host}/callback/naver`;
export const getKakaoRedirectUrl = () => `${window.location.protocol}//${window.location.host}/callback/kakao`;
export const getGoogleRedirectUrl = () => `${window.location.protocol}//${window.location.host}/callback/google`;
export const getAppleRedirectUrl = () => `${window.location.protocol}//${window.location.host}/callback/apple`;

export const SNS_AUTH_DATA_KEY = 'sns_auth_data';

export const snsAuthorizeForTypes = {
	LOGIN: 'LOGIN',
	MY_INFO_ACCESS: 'MY_INFO_ACCESS',
	CONNECT_LOGIN: 'CONNECT_LOGIN',
	DUPLICATE_LOGIN: 'DUPLICATE_LOGIN',
	WITHDRAW: 'WITHDRAW',
};

const DELAY_TIME_MS = 100;
const MAX_WAIT_TIME_MS = 5000;
const NAVER_SCRIPT_LOAD_TIMEOUT_MS = 10000;
const NAVER_LOGIN_SDK_ID = 'naver-login-sdk';

/** 네이버 로그인 클라이언트 측 에러 코드 (Sentry 구분용) */
const NAVER_ERROR_CODE = {
	/** 스크립트 로드 시간 초과 (10초 내 미로드) */
	SCRIPT_LOAD_TIMEOUT: 'script_load_timeout',
	/** SDK 초기화 시간 초과 (window.naver 5초 내 미등장) */
	SDK_INIT_TIMEOUT: 'sdk_init_timeout',
};

const getNaverErrorLogLabel = (e) => {
	if (e?.code === NAVER_ERROR_CODE.SCRIPT_LOAD_TIMEOUT) return NAVER_ERROR_CODE.SCRIPT_LOAD_TIMEOUT;
	if (e?.code === NAVER_ERROR_CODE.SDK_INIT_TIMEOUT) return NAVER_ERROR_CODE.SDK_INIT_TIMEOUT;
	return e?.response?.status ?? e?.message ?? 'error';
};

/**
 * sdk_init_timeout 발생 시 Sentry에 보낼 스크립트/환경 상태
 * - #naver-login-sdk 존재 여부, src, 로드 여부, window.naver 존재 여부
 */
const getNaverSdkInitTimeoutContext = () => {
	if (typeof document === 'undefined') return null;
	const script = document.getElementById(NAVER_LOGIN_SDK_ID);
	let scriptSrc = null;
	let scriptLoadCompleted = null;
	let scriptReadyState = null;
	if (script) {
		scriptSrc = script.getAttribute('src') ?? script.src ?? null;
		// script 태그는 onload 후 readyState가 'complete' 또는 'loaded'(IE)인 경우 있음
		scriptReadyState = script.readyState ?? null;
		// sdk_init_timeout은 loadScript가 resolve된 뒤에만 발생 → onload는 이미 발생한 상태
		scriptLoadCompleted = true;
	}
	return {
		scriptElementExists: !!script,
		scriptSrc,
		scriptLoadCompleted,
		scriptReadyState,
		windowNaverPresentAtError: typeof window !== 'undefined' && !!window.naver,
	};
};

export const getNaverLogin = () =>
	new Promise(async (resolve, reject) => {
		if (typeof window !== 'undefined') {
			try {
				const scriptLoadWithTimeout = Promise.race([
					loadScript(NAVER_LOGIN_SDK_ID, '/assets/js/sns/naveridlogin_js_sdk_2.0.0.js'),
					new Promise((_, rejectTimeout) => {
						setTimeout(() => {
							const err = new Error('네이버 SDK 스크립트 로드 시간 초과');
							err.code = NAVER_ERROR_CODE.SCRIPT_LOAD_TIMEOUT;
							rejectTimeout(err);
						}, NAVER_SCRIPT_LOAD_TIMEOUT_MS);
					}),
				]);
				await scriptLoadWithTimeout;

				/** window.naver 객체가 준비될 때까지 대기 (최대 5초) */
				const waitForNaver = () =>
					new Promise((resolveWait, rejectWait) => {
						let attempts = 0;
						const maxAttempts = MAX_WAIT_TIME_MS / DELAY_TIME_MS;

						const checkNaver = () => {
							attempts += 1;
							if (window.naver) {
								resolveWait();
							} else if (attempts >= maxAttempts) {
								const err = new Error('네이버 SDK 초기화 시간 초과');
								err.code = NAVER_ERROR_CODE.SDK_INIT_TIMEOUT;
								rejectWait(err);
							} else {
								setTimeout(checkNaver, DELAY_TIME_MS);
							}
						};
						checkNaver();
					});

				await waitForNaver();

				const { naver } = window;
				const naverLogin = new naver.LoginWithNaverId({
					clientId: NAVER_CLIENT_KEY,
					callbackUrl: getNaverRedirectUrl(),
					isPopup: true,
				});

				naverLogin.init();
				resolve(naverLogin);
			} catch (e) {
				console.error(e);
				const label = getNaverErrorLogLabel(e);
				const sentryPayload =
					e?.code === NAVER_ERROR_CODE.SDK_INIT_TIMEOUT
						? {
								error: { message: e?.message, code: e?.code, stack: e?.stack },
								sdkInitTimeoutContext: getNaverSdkInitTimeoutContext(),
							}
						: e;
				loggingData(`로그인인증(소셜) 네이버[${label}]`, sentryPayload);
				reject(e);
			}
		} else {
			reject();
		}
	});

// deprecated ~23/3/30 https://developers.google.com/identity/sign-in/web/sign-in?hl=ko
export const getGoogleLogin = () =>
	new Promise(async (resolve, reject) => {
		if (typeof window !== 'undefined') {
			try {
				await loadScript('google-login-sdk', 'https://apis.google.com/js/platform.js');
				const { gapi } = window;
				const authInstance = gapi.auth2 && gapi.auth2.getAuthInstance();

				if (authInstance) {
					resolve(authInstance);
				} else {
					gapi.load('auth2', () => {
						const auth2 = gapi.auth2.init({
							client_id: GOOGLE_CLIENT_KEY,
							scope: 'profile email',
							ux_mode: 'redirect',
							redirect_uri: getGoogleRedirectUrl(),
						});
						resolve(auth2);
					});
				}
			} catch (e) {
				console.error(e);
				loggingData(`로그인인증(소셜) 구글[${e.response?.status}]`, e);
			}
		} else {
			reject();
		}
	});

export const getAppleLogin = () =>
	new Promise(async (resolve, reject) => {
		if (typeof window !== 'undefined') {
			try {
				await loadScript('apple-login-sdk', '/assets/js/sns/appleid.auth.js');
				const { AppleID } = window;
				AppleID.auth.init({
					clientId: 'com.encar.web',
					scope: 'name email',
					redirectURI: getAppleRedirectUrl(),
				});
				resolve(AppleID.auth);
			} catch (e) {
				console.error(e);
				loggingData(`로그인인증(소셜) 애플[${e.response?.status}]`, e);
			}
		} else {
			reject();
		}
	});

/* SNS 계정 재인증 여부 확인 */
const SNS_SIGNOUT_COOKIE_KEY = 'OAUTH_SIGNOUT';

export const isSnsSignOut = (platform) => {
	const snsSignOut = getCookie(SNS_SIGNOUT_COOKIE_KEY) || '';
	const platformList = snsSignOut.split('.');

	return platformList.indexOf(platform) > -1;
};

export const setSnsSignOut = (value) => {
	setCookie(SNS_SIGNOUT_COOKIE_KEY, value, 364);
};

export const addSnsSignOut = (platform) => {
	const snsSignOut = getCookie(SNS_SIGNOUT_COOKIE_KEY) || '';

	if (snsSignOut) {
		const newSnsSignOut = snsSignOut
			.split('.')
			.concat(platform)
			.filter((p, i, arr) => arr.indexOf(p) === i)
			.join('.');

		setSnsSignOut(newSnsSignOut);
	} else {
		setSnsSignOut(platform);
	}
};

export const deleteSnsSignOut = (platform) => {
	if (platform) {
		const snsSignOut = getCookie(SNS_SIGNOUT_COOKIE_KEY) || '';
		const newSnsSignOut = snsSignOut
			.split('.')
			.filter((p) => p !== platform)
			.join('.');

		if (newSnsSignOut) {
			setSnsSignOut(newSnsSignOut);
		} else {
			deleteCookie(SNS_SIGNOUT_COOKIE_KEY);
		}
	} else {
		deleteCookie(SNS_SIGNOUT_COOKIE_KEY);
	}
};

export const closeKakaoInAppBrowser = () => {
	const {
		navigator: { userAgent },
		location,
	} = window;

	location.href = /iPad|iPhone|iPod/.test(userAgent) ? 'kakaoweb://closeBrowser' : 'kakaotalk://inappbrowser/close';
};
